// FTEDN Creator — Alerts Page (real notifications)
import { useEffect } from 'react';
import { EmptyState } from '../../components/ui/EmptyState';
import { Skeleton } from '../../components/ui/Skeleton';
import { useNotificationStore, type Notification } from '../../stores/notification.store';
import './AlertsPage.css';

const PREFERENCE_CHANNELS = [
  { key: 'inApp', label: 'In-app alerts', locked: true },
  { key: 'sms', label: 'SMS', locked: false },
  { key: 'email', label: 'Email', locked: false },
  { key: 'push', label: 'Push', locked: false },
] as const;

export function AlertsPage() {
  const {
    items,
    loading,
    unreadCount,
    preferences,
    preferencesLoading,
    preferencesSaving,
    preferencesError,
    fetchNotifications,
    fetchPreferences,
    updatePreferences,
  } = useNotificationStore();

  useEffect(() => {
    fetchNotifications();
    fetchPreferences();
  }, [fetchNotifications, fetchPreferences]);

  const typeIcons: Record<string, string> = {
    shipment: '📦', program: '🧪', community: '💬', vote: '🗳️', achievement: '🏆', level: '⚡', system: '🔔',
  };

  const typeColors: Record<string, string> = {
    shipment: 'var(--sage-green)', program: 'var(--xp-purple)', community: 'var(--navy-light)',
    vote: 'var(--xp-gold)', achievement: 'var(--xp-gold)', level: 'var(--streak-fire)', system: 'var(--sage-green)',
  };

  const groups = ['today', 'yesterday', 'earlier'];
  const groupLabels: Record<string, string> = { today: 'Today', yesterday: 'Yesterday', earlier: 'Earlier' };
  const alerts = items.map(item => ({ ...item, time: formatNotificationTime(item.createdAt), group: getAlertGroup(item.createdAt) }));

  return (
    <div className="alerts-page page-enter">
      <div className="alerts-header">
        <div>
          <h1>🔔 Alerts</h1>
          <p className="alerts-subtitle">Stay updated on everything</p>
        </div>
        {unreadCount > 0 && (
          <span className="alerts-unread-badge">{unreadCount} new</span>
        )}
      </div>

      <section className="notification-prefs" aria-labelledby="notification-prefs-title">
        <div className="notification-prefs-header">
          <h2 id="notification-prefs-title">Notification preferences</h2>
          {preferencesSaving ? (
            <span className="notification-prefs-status">Saving</span>
          ) : preferencesError ? (
            <span className="notification-prefs-status error">Needs retry</span>
          ) : null}
        </div>
        <div className="notification-prefs-grid">
          {PREFERENCE_CHANNELS.map(channel => {
            const checked = preferences.channels[channel.key];
            const disabled = preferencesLoading || preferencesSaving || channel.locked;

            return (
              <label key={channel.key} className={`notification-pref-toggle ${disabled ? 'disabled' : ''}`}>
                <span>{channel.label}</span>
                <input
                  type="checkbox"
                  role="switch"
                  aria-label={channel.label}
                  checked={checked}
                  disabled={disabled}
                  onChange={(event) => updatePreferences({
                    channels: { [channel.key]: event.currentTarget.checked },
                  })}
                />
              </label>
            );
          })}
        </div>
        <p className="notification-prefs-quiet">
          Quiet hours {formatQuietHour(preferences.quietHours.startHour)}-{formatQuietHour(preferences.quietHours.endHour)} {preferences.quietHours.timezone}
        </p>
      </section>

      {loading ? (
        <Skeleton variant="card" count={3} />
      ) : alerts.length === 0 ? (
        <EmptyState
          icon="🔔"
          title="No alerts yet"
          description="Program updates, shipments, replies, and achievements will appear here when they happen."
        />
      ) : groups.map(group => {
        const groupAlerts = alerts.filter(a => a.group === group);
        if (groupAlerts.length === 0) return null;
        return (
          <div key={group} className="alerts-group">
            <h3 className="alerts-group-label">{groupLabels[group]}</h3>
            <div className="alerts-list">
              {groupAlerts.map((a, i) => {
                const color = typeColors[a.type] ?? typeColors.system;
                return (
                  <div
                    key={a.id}
                    className={`alert-card ${a.read ? 'read' : 'unread'}`}
                    style={{ animationDelay: `${i * 0.06}s` }}
                  >
                    <div className="alert-icon-wrap" style={{
                      background: `${color}15`,
                      boxShadow: `0 0 12px ${color}20`,
                    }}>
                      <span className="alert-icon">{typeIcons[a.type] ?? typeIcons.system}</span>
                    </div>
                    <div className="alert-body">
                      <h4 className="alert-title">{a.title}</h4>
                      <p className="alert-text">{a.body}</p>
                    </div>
                    <div className="alert-right">
                      <span className="alert-time">{a.time}</span>
                      {a.xp > 0 && <span className="alert-xp">+{a.xp} XP</span>}
                    </div>
                    {!a.read && <div className="alert-unread-dot" />}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatQuietHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

function getAlertGroup(createdAt: string): 'today' | 'yesterday' | 'earlier' {
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return 'earlier';

  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startYesterday = startToday - 24 * 60 * 60 * 1000;
  const createdTime = created.getTime();

  if (createdTime >= startToday) return 'today';
  if (createdTime >= startYesterday) return 'yesterday';
  return 'earlier';
}

function formatNotificationTime(createdAt: Notification['createdAt']): string {
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return '';

  const diffMs = Date.now() - created.getTime();
  const diffMinutes = Math.max(0, Math.floor(diffMs / 60000));
  if (diffMinutes < 1) return 'now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}
